import { read, utils } from 'xlsx';
import type { MapeigDto } from '@/api/administracio';

export type CampImport = 'nom' | 'email' | 'telefon' | 'canals';
export const CAMPS_IMPORT: CampImport[] = ['nom', 'email', 'telefon', 'canals'];
export const CAMPS_OBLIGATORIS: CampImport[] = ['nom', 'email'];

export interface FullExcel {
  columnes: string[];
  files: Record<string, string>[];
}

export interface ErrorFila {
  fila: number;
  camp: CampImport;
  codi: 'obligatori' | 'email' | 'duplicat' | 'telefon';
}

export interface ResultatValidacio {
  total: number;
  valids: number;
  duplicats: number;
  errors: ErrorFila[];
}

const SINONIMS: Record<CampImport, string[]> = {
  nom: ['nom', 'nombre', 'name', 'nom complet', 'nombre completo', 'persona'],
  email: ['email', 'e-mail', 'correu', 'correo', 'mail', 'correu electronic', 'correo electronico'],
  telefon: ['telefon', 'telefono', 'mobil', 'movil', 'phone', 'tel'],
  canals: ['canals', 'canales', 'canal', 'channels'],
};

const normalitza = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TELEFON = /^\+?[0-9 ()-]{6,20}$/;

export async function llegeixExcel(fitxer: File): Promise<FullExcel> {
  const llibre = read(await fitxer.arrayBuffer());
  const full = llibre.Sheets[llibre.SheetNames[0] ?? ''];
  if (!full) return { columnes: [], files: [] };
  return deMatriu(utils.sheet_to_json<unknown[]>(full, { header: 1, defval: '' }));
}

export function deMatriu(matriu: unknown[][]): FullExcel {
  const [capcalera = [], ...resta] = matriu;
  const columnes = capcalera.map((c) => String(c ?? '').trim()).filter((c) => c !== '');
  const files = resta
    .filter((f) => f.some((v) => String(v ?? '').trim() !== ''))
    .map((f) => {
      const fila: Record<string, string> = {};
      columnes.forEach((c, i) => {
        fila[c] = String(f[i] ?? '').trim();
      });
      return fila;
    });
  return { columnes, files };
}

export function suggereixMapeig(columnes: string[]): Partial<Record<CampImport, string>> {
  const mapeig: Partial<Record<CampImport, string>> = {};
  for (const camp of CAMPS_IMPORT) {
    const trobada = columnes.find((c) => SINONIMS[camp].includes(normalitza(c)));
    if (trobada) mapeig[camp] = trobada;
  }
  return mapeig;
}

export function mapeigComplet(
  m: Partial<Record<CampImport, string>>,
): m is Partial<Record<CampImport, string>> & MapeigDto {
  return CAMPS_OBLIGATORIS.every((c) => !!m[c]);
}

export function validaFiles(
  files: Record<string, string>[],
  m: Partial<Record<CampImport, string>>,
): ResultatValidacio {
  const errors: ErrorFila[] = [];
  const vistos = new Set<string>();
  let valids = 0;
  let duplicats = 0;
  files.forEach((f, i) => {
    const fila = i + 2;
    const nom = m.nom ? (f[m.nom] ?? '') : '';
    const email = m.email ? (f[m.email] ?? '').toLowerCase() : '';
    const telefon = m.telefon ? (f[m.telefon] ?? '') : '';
    const propis: ErrorFila[] = [];
    if (!nom) propis.push({ fila, camp: 'nom', codi: 'obligatori' });
    if (!email) propis.push({ fila, camp: 'email', codi: 'obligatori' });
    else if (!EMAIL.test(email)) propis.push({ fila, camp: 'email', codi: 'email' });
    if (telefon && !TELEFON.test(telefon)) propis.push({ fila, camp: 'telefon', codi: 'telefon' });
    if (propis.length === 0 && vistos.has(email)) {
      duplicats++;
      propis.push({ fila, camp: 'email', codi: 'duplicat' });
    }
    if (email) vistos.add(email);
    if (propis.length === 0) valids++;
    errors.push(...propis);
  });
  return { total: files.length, valids, duplicats, errors };
}
